import { useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  User, Envelope, Phone, Shield, Pencil, Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getRoleColor = (role) => {
  switch (role) {
    case "admin": return "bg-purple-100 text-purple-700";
    case "customer": return "bg-indigo-100 text-indigo-700";
    case "tenant": return "bg-emerald-100 text-emerald-700";
    default: return "bg-slate-100 text-slate-700";
  }
};

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();

  const getRoleName = (role) => {
    switch (role) {
      case "admin": return t("common.role_admin");
      case "customer": return t("common.role_customer");
      case "tenant": return t("common.role_tenant");
      case "staff": return t("common.role_staff");
      default: return role;
    }
  };
  const [editOpen, setEditOpen] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({ name: "", phone: "" });
  const [passData, setPassData] = useState({ current_password: "", new_password: "", confirm: "" });

  const openEditDialog = () => {
    setEditData({ name: user?.name || "", phone: user?.phone || "" });
    setEditOpen(true);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, editData);
      toast.success("Profil guncellendi");
      setEditOpen(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata olustu");
    } finally { setLoading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passData.new_password !== passData.confirm) {
      toast.error("Yeni sifreler eslesmiyor");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: passData.current_password,
        new_password: passData.new_password,
      });
      toast.success("Sifre basariyla degistirildi");
      setPassOpen(false);
      setPassData({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata olustu");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      {/* Profile Header */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-indigo-100 rounded-2xl flex items-center justify-center">
              <span className="text-3xl font-bold text-indigo-600">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-heading font-bold text-slate-900">{user?.name}</h1>
              <p className="text-slate-500 mt-1">{user?.email}</p>
              <Badge className={`mt-2 ${getRoleColor(user?.role)}`}>{getRoleName(user?.role)}</Badge>
            </div>
            {user?.role === "tenant" && (
              <div className="text-right">
                <p className="text-sm text-slate-500">Bakiye</p>
                <p className="text-2xl font-bold text-emerald-600">₺{user?.balance?.toFixed(2) || "0.00"}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Profile Details */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading">Profil Bilgileri</CardTitle>
          <Button variant="outline" size="sm" onClick={openEditDialog} data-testid="edit-profile-btn">
            <Pencil size={14} className="mr-2" /> Duzenle
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
            <User size={24} className="text-slate-400" />
            <div className="flex-1">
              <Label className="text-xs text-slate-400">{t("common.name")}</Label>
              <p className="font-medium text-slate-900">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
            <Envelope size={24} className="text-slate-400" />
            <div className="flex-1">
              <Label className="text-xs text-slate-400">{t("common.email")}</Label>
              <p className="font-medium text-slate-900">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
            <Phone size={24} className="text-slate-400" />
            <div className="flex-1">
              <Label className="text-xs text-slate-400">{t("common.phone")}</Label>
              <p className="font-medium text-slate-900">{user?.phone || "Belirtilmemis"}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
            <Shield size={24} className="text-slate-400" />
            <div className="flex-1">
              <Label className="text-xs text-slate-400">Hesap Turu</Label>
              <p className="font-medium text-slate-900">{getRoleName(user?.role)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password & Account */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Guvenlik</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-3">
              <Lock size={24} className="text-slate-400" />
              <div>
                <p className="font-medium text-slate-900">{t("profile.change_password")}</p>
                <p className="text-xs text-slate-500">Hesap sifrenizi guncelleyin</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setPassOpen(true)} data-testid="change-password-btn">
              Degistir
            </Button>
          </div>
          <Separator />
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Hesap ID</span>
              <span className="font-mono text-slate-600">{user?.id?.slice(0, 8)}...</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Kayit Tarihi</span>
              <span className="text-slate-600">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" }) : "-"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Permissions - Only for tenants */}
      {user?.role === "tenant" && user?.permissions && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-lg font-heading">Izinler</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(user.permissions).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-600">{key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <Badge className={val ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                    {val ? t("staff_perm.has_permission") : t("staff_perm.no_permission")}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t("profile.edit_title")}</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateProfile}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">{t("common.name")}</Label>
                <Input id="edit-name" value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  required data-testid="edit-profile-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">{t("common.phone")}</Label>
                <Input id="edit-phone" value={editData.phone}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  data-testid="edit-profile-phone" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading} data-testid="edit-profile-submit">
                {loading ? "Kaydediliyor..." : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passOpen} onOpenChange={setPassOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t("profile.change_password")}</DialogTitle></DialogHeader>
          <form onSubmit={handleChangePassword}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="current-pass">{t("common.current_password")}</Label>
                <Input id="current-pass" type="password" value={passData.current_password}
                  onChange={(e) => setPassData({ ...passData, current_password: e.target.value })}
                  required data-testid="current-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-pass">{t("common.new_password")}</Label>
                <Input id="new-pass" type="password" value={passData.new_password}
                  onChange={(e) => setPassData({ ...passData, new_password: e.target.value })}
                  required data-testid="new-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pass">{t("common.confirm_password")}</Label>
                <Input id="confirm-pass" type="password" value={passData.confirm}
                  onChange={(e) => setPassData({ ...passData, confirm: e.target.value })}
                  required data-testid="confirm-password-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPassOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading} data-testid="change-password-submit">
                {loading ? t("common.updating") : t("common.update")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
